import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'

import { Flex, Image, P, ResponsiveSVG } from "ui/acro";
import { Text } from "ui/bend";
import { axios } from "./../../../api";
import PlaylistInfo from "./PlaylistInfo";
import PlaylistClass from "./PlaylistClass";
import EmptyPlaylist from "./EmptyPlaylist";
import ReactSortable from "react-sortablejs";
import chevronSVG from "../../../../../app/assets/images/chevron.svg";
import InfiniteScroll from 'react-infinite-scroller'

const BackButtonContainer = styled(Flex)`
  justify-content: space-between;
  align-items: center;
`
const ContentWrapper = styled(Flex)`
  flex-direction: column;
  gap: 24px;
  ${(props) => props.theme.mediaQueries.small} {
    flex-direction: row;
  }
`
const DeleteText = styled(Text)`
  color: ${(props) => props.theme.colors.alert.medium};
  font-family: ${(props) => props.theme.fonts.Proxima};
  font-size: 16px;
  font-weight: 400;
  line-height: 24px; /* 150% */
  letter-spacing: 0.352px;
  display: block;
  cursor: pointer;
  ${(props) => props.theme.mediaQueries.small} {
    display: none;
  }
`
const BackButtonText = styled(Text)`
  color: ${(props) => props.theme.colors.black};
  font-family: ${(props) => props.theme.fonts.Proxima};
  font-size: 18px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px; /* 133.333% */
  letter-spacing: 0.396px;
  display: none;
  ${(props) => props.theme.mediaQueries.small} {
    display: block;
  }
`;
 
const PlaceholderBox = styled.div`
  width: 20%;
  height: 118px;
  @media (max-width: 1024px) {
    width: 30%;
  }
`;
 
const PlaceholderContainer = styled.div`
  display: flex;
  margin-top: 24px;
  flex-direction: column;
  gap: 24px;
`;
 
const PlaceholderFiller = styled.div`
  background: #ebebeb;
  padding-top: 56.25%;
`;
 
const PlaylistDetailsPage = ({ playlistObj, userIsAuthor, signedIn, source }) => {
  const [playlist, setPlaylist] = useState(playlistObj);
  const [listOfClasses, setListOfClasses] = useState([]);
  const [updateClassList, setUpdateClassList] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
 
  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
   if(updateClassList){
    deleteFetchData();
    setUpdateClassList(false);
  }
  }, [updateClassList]);

  const coverPhoto = playlist?.coverPhoto?.url
    ? playlist?.coverPhoto?.url
    : playlist?.firstEntry?.videoThumbnail
  const {
    author,
    description,
    entryCount,
    title,
    totalDuration,
    uuid,
    following
  } = playlist

  const loadingCards = new Array(4).fill(null).map((card, idx) => (
    <PlaceholderContainer>
      <PlaceholderBox key={idx}>
        <PlaceholderFiller />
      </PlaceholderBox>
    </PlaceholderContainer>
    
  ));

  const fetchData = async (currentPage) => {
    let params = {
      per_page: 10,
      page: currentPage,
    };
    try {
      const res = await axios.get(`/playlists/${playlist?.uuid}/entries`, {
        params
      })
      if (res.status === 200) {
        setListOfClasses((prev) => [...prev, ...res?.data]);
        if (res?.data?.length  < params.per_page) {
          setHasMore(false); // No more data to load
          return;
        }
        else{
          setPage(prevPage => prevPage + 1);
        }
      }
    } catch (e) {
      console.error(e);
    }
  };
 
  const deleteFetchData = async () => {
    let params = {
      per_page: hasMore ? 10 * (page - 1) : 10 * page ,
      page: 1,
    };
    try {
      const res = await axios.get(`/playlists/${playlist?.uuid}/entries`, {
        params,
      });
      if (res?.data?.length) {
        setListOfClasses(res?.data);
        if (res?.data?.length  < params.per_page) {
          setHasMore(false); // No more data to load
          return;
        }
      //  setPage(prevPage => prevPage + 1);
      }
    } catch (e) {
      console.error(e)
    }
  }

  const onSortEnd = async ({ oldIndex, newIndex }) => {
    const updatedList = [...listOfClasses]
    const [removedItem] = updatedList.splice(oldIndex, 1)
    updatedList.splice(newIndex, 0, removedItem)
    let planEntryIds = updatedList.map((classItem) => classItem.id)

    try {
      const res = await axios.post(`/playlists/${playlist?.uuid}/sort`, {
        plan_entry: planEntryIds
      })
      if (res.status === 200) {
        setListOfClasses(updatedList)
      }
    } catch (e) {
      console.error(e)
    }
  }

  const getBackButtonText = () => {
    let BackButtonText = ''

    if (source === 'mypractice' || document.referrer.includes('/my/practice')) {
      BackButtonText = 'Back to My Playlists'
    } else if (
      source === 'playlists' ||
      document.referrer.includes('/playlists')
    ) {
      BackButtonText = 'Back to All Playlists'
    }

    return BackButtonText
  }

  const getBackUrl = () => {
    let backurl = ''

    if (source === 'mypractice' || document.referrer.includes('/my/practice')) {
      backurl = '/my/practice?tab=playlists'
    } else if (
      source === 'playlists' ||
      document.referrer.includes('/playlists')
    ) {
      backurl = '/playlists'
    }

    return backurl
  }

  const deletePlaylist = async () => {
    try {
      const res = await axios.delete(`/playlists/${uuid}`)
      if (res.status === 200) {
        window.location.href = document.referrer
      }
    } catch (error) {
      console.error(error)
    }
  }

  const onEditPlaylist = async () => {
    try {
      const res = await axios.get(`/playlists/${uuid}`)
      if (res.status === 200) {
        setPlaylist(res.data)
      }
    } catch (error) {
      console.error(error)
    }
  }

  return (
    <Flex
      flexDirection='column'
      gap='16px'
      pt={['0px', '16px', '16px', '16px']}
      pl={['16px', '16px', '16px', '105px']}
      pr={['16px', '16px', '16px', '105px']}
      mb='100px'
    >
      {(document.referrer || ['mypractice', 'playlists'].includes(source)) && (
        <BackButtonContainer>
          <Flex gap='8px' as='a' href={getBackUrl()}>
            <ResponsiveSVG
              SVG={chevronSVG}
              width={['20px']}
              height={['20px']}
            />
            <BackButtonText>{getBackButtonText()}</BackButtonText>
          </Flex>
          {userIsAuthor && (
            <DeleteText onClick={deletePlaylist}>Delete</DeleteText>
          )}
        </BackButtonContainer>
      )}
      <ContentWrapper>
        <PlaylistInfo
          coverPhoto={coverPhoto}
          description={description}
          classCount={entryCount}
          author={author}
          title={title}
          totalDuration={totalDuration}
          uuid={uuid}
          userIsAuthor={userIsAuthor}
          following={following}
          signedIn={signedIn}
          onDelete={deletePlaylist}
          onEdit={onEditPlaylist}
        />
        {listOfClasses.length > 0 ? (
          <InfiniteScroll
            pageStart={1}
            loadMore={fetchData}
            hasMore={hasMore}
            loader={loadingCards}
            threshold={400}
            style={{flex: '1 1 0%'}}
          >
            <ReactSortable
              onChange={(items, __, evt) => {
                onSortEnd(evt);
              }}
              options={{
                animation: 150,
                // disabled: !sortable
              }}
              className="sortable-list"
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "24px",
                flex: "1",
              }}
            >
              {listOfClasses.map((classItem) => {
                return (
                  <PlaylistClass
                    classObject={classItem}
                    author={author}
                    key={classItem.id}
                    data-id={classItem.id}
                    userIsAuthor={userIsAuthor}
                    uuid={uuid}
                    setUpdateClassList={setUpdateClassList}
                  />
                );
              })}
            </ReactSortable>
          </InfiniteScroll>
        ) : (
          <EmptyPlaylist />
        )}
      </ContentWrapper>
    </Flex>
  )
}
PlaylistDetailsPage.propTypes = {}

export default PlaylistDetailsPage
